import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import CIcon from "@coreui/icons-react";
import { cilChevronRight, cilExternalLink } from "@coreui/icons";

type Target =
  | { to: string; state?: unknown }
  | { href: string }
  | { onClick: () => void }
  | { muted: true };

type StrategyColumnLinkProps = Target & {
  icon: ReactNode;
  children: ReactNode;
  /** Secondary detail after the label, such as the TradingView account a script is on. */
  meta?: string;
};

/**
 * A row in the short list under a strategy page's price or subscription buttons. A muted row is a status with
 * nothing to do yet, and says why in words because a tooltip never shows on touch.
 */
export function StrategyColumnLink(props: StrategyColumnLinkProps) {
  const { icon, children, meta } = props;
  const body = (end: ReactNode) => (
    <>
      <span className="strategy-column-link-icon">{icon}</span>
      <span className="strategy-column-link-label">
        {children}
        {meta && <span className="strategy-column-link-meta">{meta}</span>}
      </span>
      {end}
    </>
  );
  const chevron = <CIcon icon={cilChevronRight} className="card-link-chevron" aria-hidden />;

  if ("to" in props) {
    return (
      <Link to={props.to} state={props.state} className="strategy-column-link">
        {body(chevron)}
      </Link>
    );
  }
  if ("href" in props) {
    return (
      <a href={props.href} target="_blank" rel="noopener noreferrer" className="strategy-column-link">
        {body(<CIcon icon={cilExternalLink} className="card-link-chevron" aria-hidden />)}
      </a>
    );
  }
  if ("onClick" in props) {
    return (
      <button type="button" onClick={props.onClick} className="strategy-column-link">
        {body(chevron)}
      </button>
    );
  }
  return <div className="strategy-column-link is-muted">{body(null)}</div>;
}

/** Keeps the rows flush with each other inside the column's gapped stack. */
export function StrategyColumnLinks({ children }: { children: ReactNode }) {
  return <div className="strategy-column-links">{children}</div>;
}
